import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useAuth } from './AuthProvider';
import { useOktaAuthInstance } from './AuthProvider';
import { useCadConfig } from '../cad/cad-config';

interface LoginCallbackProps {
  /** Element shown while the callback is being processed */
  loadingElement?: ReactNode;
  /** Element shown if the callback fails */
  errorElement?: (error: string) => ReactNode;
}

const DefaultLoading = ({ status }: { status: string }) => (
  <Box
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
    gap={2}
  >
    <CircularProgress />
    <Typography variant="h6">{status}</Typography>
  </Box>
);

/**
 * Unified login callback that handles both Okta and CAD redirect flows.
 *
 * - For Okta: invokes `oktaAuth.handleLoginRedirect()` which exchanges the
 *   code for tokens; the Security wrapper's `restoreOriginalUri` then
 *   navigates back to the original location.
 * - For CAD: the CadAuthProvider handles `?code=&state=` itself during init,
 *   so this component just waits for `auth.isPending` to clear and then
 *   redirects to the app root (CadAuthProvider has already restored the URL).
 */
export function LoginCallback({
  loadingElement,
  errorElement,
}: LoginCallbackProps = {}) {
  const auth = useAuth();
  const oktaAuth = useOktaAuthInstance();
  const cadConfig = useCadConfig();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('Processing authentication...');

  // Drive the Okta callback as soon as we have an OktaAuth instance.
  useEffect(() => {
    if (cadConfig.enabled || !oktaAuth) return;

    let cancelled = false;
    const run = async () => {
      try {
        setStatus('Completing sign in...');
        // handleLoginRedirect is idempotent in @okta/okta-react v6+; calling it
        // when no callback is present is a no-op.
        if (oktaAuth.token.isLoginRedirect()) {
          await oktaAuth.handleLoginRedirect();
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        // PKCE/duplicate-flow errors usually mean the user is already
        // authenticated — verify before reporting.
        try {
          const isAuthenticated = await oktaAuth.isAuthenticated();
          if (isAuthenticated) {
            navigate('/', { replace: true });
            return;
          }
        } catch {
          /* fall through */
        }
        setError(message);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [cadConfig.enabled, oktaAuth, navigate]);

  // For CAD, the CadAuthProvider performs the redirect itself once the
  // callback completes. If we get here and CAD is no longer pending and the
  // URL still has /login/callback, fall back to navigating home.
  useEffect(() => {
    if (!cadConfig.enabled) return;
    const onCadCallback = window.location.pathname.includes('/Load/login/callback');
    if (auth.isPending) {
      setStatus('Completing sign in...');
      return;
    }
    // Load hosts the shared CAD callback; CadAuthProvider redirects the user
    // back to the initiating app (may be another host/port), so don't
    // navigate here.
    if (auth.isAuthenticated && !onCadCallback) {
      navigate('/', { replace: true });
    }
  }, [auth.isAuthenticated, auth.isPending, cadConfig.enabled, navigate]);

  if (error) {
    if (errorElement) return <>{errorElement(error)}</>;
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
        p={3}
      >
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (loadingElement) return <>{loadingElement}</>;
  return <DefaultLoading status={status} />;
}
